import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { ApiError } from '@/api/client'
import { adminApi } from '@/api/workspaces'
import { RefreshFailed } from '@/components/RefreshFailed'
import { adminCopy } from '@/copy'
import { useMutationToast } from '@/hooks/useMutationToast'
import { useAuthStore } from '@/store/auth'
import type { UserInfo } from '@/types/api'
import { Alert } from '@/ui/Alert/Alert'
import { Avatar } from '@/ui/Avatar/Avatar'
import { Button } from '@/ui/Button/Button'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { TextField } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Grid } from '@/ui/layout/Grid'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import styles from './UsersTab.module.css'

export function UsersTab() {
  const qc = useQueryClient()
  const id = useId()
  const currentUserId = useAuthStore((s) => s.userId)
  const [creating, setCreating] = useState(false)
  const [newUsername, setNewUsername] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  const usersQuery = useQuery<UserInfo[]>({
    queryKey: ['admin', 'users'],
    queryFn: adminApi.listUsers,
  })
  const users = usersQuery.data ?? []
  const hasData = usersQuery.data !== undefined
  const showSkeleton = useSkeletonDelay(usersQuery.isPending)
  const userToDelete = users.find((u) => u.user_id === confirmDelete)

  const createMutation = useMutationToast({
    mutationFn: ({
      username,
      password,
    }: {
      username: string
      password: string
    }) => adminApi.createUser(username, password),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      setCreating(false)
      setNewUsername('')
      setNewPassword('')
    },
  })

  const deleteMutation = useMutationToast({
    mutationFn: (userId: string) => adminApi.deleteUser(userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      setConfirmDelete(null)
    },
    errorMessage: adminCopy.users.deleteFailed,
  })

  const toggleAdminMutation = useMutationToast({
    mutationFn: ({ userId, isAdmin }: { userId: string; isAdmin: boolean }) =>
      adminApi.updateUser(userId, { is_system_admin: isAdmin }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
    // 422 is the server refusing to revoke your own or the last admin.
    errorMessage: (error, { userId }) =>
      !(error instanceof ApiError && error.status === 422)
        ? adminCopy.users.adminFailed
        : userId === currentUserId
          ? adminCopy.users.removeOwnAdminFailed
          : adminCopy.users.lastAdminFailed,
  })

  function submitCreate(e: React.FormEvent) {
    e.preventDefault()
    if (newUsername.trim() && newPassword.trim()) {
      createMutation.mutate({
        username: newUsername.trim(),
        password: newPassword,
      })
    }
  }

  const createForm = (
    <form onSubmit={submitCreate}>
      <Stack gap="space-16">
        <Grid columns={1} mdColumns={2} gap="space-16">
          <TextField
            id={`${id}-username`}
            label={adminCopy.users.username}
            value={newUsername}
            onValue={setNewUsername}
          />
          <TextField
            id={`${id}-password`}
            label={adminCopy.users.password}
            value={newPassword}
            onValue={setNewPassword}
            inputProps={{ type: 'password' }}
          />
        </Grid>
        <Inline gap="space-8">
          <Button
            type="submit"
            variant="primary"
            disabled={!newUsername.trim() || !newPassword.trim()}
            loading={createMutation.isPending}
          >
            {adminCopy.users.create}
          </Button>
          <Button variant="outline" onClick={() => setCreating(false)}>
            {adminCopy.users.cancel}
          </Button>
        </Inline>
        {createMutation.isError && (
          <Alert tone="danger">{adminCopy.users.createFailed}</Alert>
        )}
      </Stack>
    </form>
  )
  const addButton = (
    <Button variant="ghost" onClick={() => setCreating(true)}>
      <Icon name="plus" size="icon-embedded" />
      {adminCopy.users.add}
    </Button>
  )

  return (
    <section>
      <SectionHeader rule trailingSpacing="section">
        {adminCopy.users.heading}
      </SectionHeader>

      <Stack gap="space-12" aria-busy={usersQuery.isPending}>
        {usersQuery.isPending ? (
          showSkeleton && <Skeleton lines={3} />
        ) : usersQuery.isError && !hasData ? (
          <Alert tone="danger">
            <Inline gap="space-8" align="baseline" wrap>
              <Text>{adminCopy.loadFailed.users}</Text>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void usersQuery.refetch()}
              >
                {adminCopy.retry}
              </Button>
            </Inline>
          </Alert>
        ) : (
          <>
            {usersQuery.isError && (
              <RefreshFailed
                message={adminCopy.refreshFailed.users}
                onRetry={() => void usersQuery.refetch()}
              />
            )}
            {users.length === 0 && !creating && (
              <EmptyState
                variant="inline"
                description={adminCopy.users.empty}
              />
            )}
            <RowList aria-label={adminCopy.users.listLabel}>
              {users.map((user, index) => (
                <RowListItem key={user.user_id}>
                  <Avatar
                    name={user.username}
                    kind="human"
                    size="md"
                    alt={index % 2 === 1}
                  />
                  <Text tone="text-sub" className={styles.grow}>
                    {user.username}
                  </Text>
                  {user.is_system_admin && (
                    <Text tone="text-mute">{adminCopy.users.adminRole}</Text>
                  )}
                  <Inline gap="space-8" wrap={false}>
                    <Button
                      variant="ghost"
                      loading={
                        toggleAdminMutation.isPending &&
                        toggleAdminMutation.variables?.userId === user.user_id
                      }
                      disabled={
                        user.is_system_admin && user.user_id === currentUserId
                      }
                      onClick={() =>
                        toggleAdminMutation.mutate({
                          userId: user.user_id,
                          isAdmin: !user.is_system_admin,
                        })
                      }
                    >
                      {user.is_system_admin
                        ? adminCopy.users.removeAdmin
                        : adminCopy.users.makeAdmin}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setConfirmDelete(user.user_id)}
                    >
                      {adminCopy.users.delete}
                    </Button>
                  </Inline>
                </RowListItem>
              ))}
              <RowListItem className={creating ? styles.form : undefined}>
                {creating ? createForm : addButton}
              </RowListItem>
            </RowList>
          </>
        )}
      </Stack>

      <ConfirmDialog
        open={confirmDelete !== null}
        title={adminCopy.users.deleteDialog.title}
        tone="danger"
        confirmLabel={adminCopy.users.deleteDialog.confirm}
        description={
          userToDelete
            ? adminCopy.users.deleteDialog.description(userToDelete.username)
            : undefined
        }
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (confirmDelete) deleteMutation.mutate(confirmDelete)
        }}
        onClose={() => setConfirmDelete(null)}
      />
    </section>
  )
}
